import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, Lightbulb, AlertTriangle, HelpCircle, BookOpen, GitCompare, ArrowRightCircle } from 'lucide-react';
import { useSessionGuidance, useAddGuidance, useRemoveGuidance } from '@/hooks/useOptiBayGuidance';
import { useSessionEvidence } from '@/hooks/useOptiBayEvidence';
import { OptiBayTrainingHints } from './OptiBayTrainingHints';
import { generateGuidanceHints, type GuidanceHint } from '@/utils/optibay/generateGuidanceHints';
import type { OptiBaySession, OptiBaySessionGuidance, OptiBayGuidanceType } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayGuidanceStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

const GUIDANCE_TYPE_CONFIG: Record<OptiBayGuidanceType, { label: string; icon: typeof Lightbulb; color: string }> = {
  pattern_hint: { label: 'Pattern Hint', icon: Lightbulb, color: 'text-amber-500' },
  diagnostic_question: { label: 'Diagnostic Question', icon: HelpCircle, color: 'text-blue-500' },
  spec_reference: { label: 'Spec Reference', icon: BookOpen, color: 'text-primary' },
  safety_warning: { label: 'Safety Warning', icon: AlertTriangle, color: 'text-destructive' },
};

const HINT_TYPE_ICON: Record<GuidanceHint['type'], { icon: typeof Lightbulb; color: string }> = {
  pattern_hint: { icon: Lightbulb, color: 'text-amber-500' },
  comparison_prompt: { icon: GitCompare, color: 'text-blue-500' },
  next_verification: { icon: ArrowRightCircle, color: 'text-primary' },
};

export function OptiBayGuidanceStep({ session, onNext, onBack }: OptiBayGuidanceStepProps) {
  const { data: guidance = [] } = useSessionGuidance(session.id);
  const { data: evidence = [] } = useSessionEvidence(session.id);
  const addGuidance = useAddGuidance();
  const removeGuidance = useRemoveGuidance();

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [guidanceType, setGuidanceType] = useState<OptiBayGuidanceType>('pattern_hint');

  const evidenceGateMet = evidence.length > 0;
  const autoHints = generateGuidanceHints(evidence);

  const handleAdd = async () => {
    if (!title.trim() || !body.trim()) return;
    await addGuidance.mutateAsync({
      sessionId: session.id,
      guidanceType,
      title: title.trim(),
      body: body.trim(),
      evidenceGateMet,
      sortOrder: guidance.length,
    });
    setTitle('');
    setBody('');
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Diagnostic Guidance</h4>
        <Badge variant={evidenceGateMet ? 'default' : 'secondary'} className="text-xs">
          {evidenceGateMet ? 'Evidence Available' : 'No Evidence'}
        </Badge>
      </div>

      {!evidenceGateMet && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5" />
          Guidance is most effective with evidence collected. Consider going back to add evidence first.
        </div>
      )}

      {/* Auto-generated interpretation support */}
      {autoHints.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Interpretation Support
          </p>
          {autoHints.map((hint, i) => {
            const cfg = HINT_TYPE_ICON[hint.type];
            const Icon = cfg.icon;
            return (
              <div key={i} className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={cn('h-3.5 w-3.5 shrink-0', cfg.color)} />
                  <span className="text-xs font-semibold">{hint.title}</span>
                </div>
                <p className="text-xs text-muted-foreground pl-5">{hint.body}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Manual guidance entries */}
      {guidance.length === 0 && !showForm && autoHints.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No guidance notes yet. Add pattern observations or diagnostic questions.
        </p>
      ) : guidance.length > 0 ? (
        <div className="space-y-2">
          {guidance.length > 0 && autoHints.length > 0 && (
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              Manual Notes
            </p>
          )}
          {guidance.map(g => {
            const config = GUIDANCE_TYPE_CONFIG[g.guidance_type as OptiBayGuidanceType] || GUIDANCE_TYPE_CONFIG.pattern_hint;
            const Icon = config.icon;
            return (
              <div key={g.id} className="border rounded-lg p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Icon className={cn('h-3.5 w-3.5 shrink-0', config.color)} />
                    <span className="text-xs font-semibold">{g.title}</span>
                    <Badge variant="outline" className="text-[10px]">{config.label}</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
                    onClick={() => removeGuidance.mutate({ id: g.id, sessionId: session.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground pl-5">{g.body}</p>
              </div>
            );
          })}
        </div>
      ) : null}

      {showForm ? (
        <div className="border rounded-lg p-3 space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {(Object.keys(GUIDANCE_TYPE_CONFIG) as OptiBayGuidanceType[]).map(type => {
              const cfg = GUIDANCE_TYPE_CONFIG[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setGuidanceType(type)}
                  className={cn(
                    'text-[10px] px-2 py-1 rounded-full border font-medium transition-colors',
                    guidanceType === type
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-accent/50 text-muted-foreground',
                  )}
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>
          <Input
            placeholder="Guidance title…"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="text-xs h-8"
          />
          <Textarea
            placeholder="Describe the pattern, question, or observation…"
            value={body}
            onChange={e => setBody(e.target.value)}
            className="text-xs min-h-[60px]"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAdd} disabled={!title.trim() || !body.trim() || addGuidance.isPending}>
              Add Guidance
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Guidance Note
        </Button>
      )}

      <OptiBayTrainingHints session={session} className="mt-2" />

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" onClick={onNext}>
          Continue to Verification
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
